import React, { useState, useMemo, useEffect } from 'react';
import SupervisorLayout from '../../components/SupervisorLayout';
import SupervisorMoveDetailView from './SupervisorMoveDetailView';
import { fetchSupervisorSalesMonitoringSummary } from '../../api/supervisor';
import './SupervisorSalesMonitoringResults.css';

const sortData = (data) => [...data].sort((a, b) => {
  if (a.game !== b.game) return a.game.localeCompare(b.game);
  if (b.value !== a.value) return b.value - a.value;
  return parseInt(a.numero, 10) - parseInt(b.numero, 10);
});

const formatDateDisplay = (d) => {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
};

const SupervisorSalesMonitoringResults = ({ date, tirage, bankId, groupId, onBack }) => {
  const [selectedRow, setSelectedRow] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchSupervisorSalesMonitoringSummary({ tirage, date, bank: bankId, group: groupId })
      .then((data) => { if (!cancelled) setSummary(data); })
      .catch(() => { if (!cancelled) setError('Impossible de charger les ventes — verifie que le serveur backend tourne.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tirage, date, bankId, groupId]);

  const productSummary = summary ? summary.product_summary : [];
  const lotteryData = summary ? summary.lottery_summary : [];
  const tirageName = summary ? summary.tirage_name : '';

  const sortedData = useMemo(() => sortData(summary ? summary.numbers : []), [summary]);

  if (selectedRow) {
    return (
      <SupervisorMoveDetailView
        row={selectedRow}
        date={formatDateDisplay(date)}
        tirage={tirage}
        tirageName={tirageName}
        bankId={bankId}
        groupId={groupId}
        rawDate={date}
        onBack={() => setSelectedRow(null)}
      />
    );
  }

  const totalValue = sortedData.reduce((s, r) => s + r.value, 0);
  const totalBets = sortedData.reduce((s, r) => s + r.bets, 0);
  const totalProduct = productSummary.reduce((s, r) => s + r.value, 0);
  const totalLottery = lotteryData.reduce((s, r) => s + r.total, 0);
  const totalLocal = lotteryData.reduce((s, r) => s + r.local, 0);

  return (
    <SupervisorLayout>
      <div className="svsmr-info-bar">
        Date : {formatDateDisplay(date)} | Lottery : {tirageName} | :
      </div>

      {error && <p className="svsmr-error">{error}</p>}
      {loading && <p className="svsmr-loading">Chargement...</p>}

      {!loading && !error && (
        <div className="svsmr-body">
          <div className="svsmr-left">
            <table className="svsmr-table">
              <thead>
                <tr>
                  <th>Game</th>
                  <th>Numeros</th>
                  <th>Value</th>
                  <th>Number of Bets</th>
                  <th>Average</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row, i) => (
                  <tr key={i}>
                    <td>{row.game}</td>
                    <td>
                      <span className="svsmr-numero svsmr-numero-link" onClick={() => setSelectedRow(row)}>
                        {row.numero}
                      </span>
                    </td>
                    <td className={row.hot ? 'svsmr-hot' : ''}>{row.value.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td>{row.bets}</td>
                    <td>{row.avg.toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="svsmr-total-row">
                  <td colSpan="2"><strong>Total</strong></td>
                  <td className="svsmr-hot"><strong>{totalValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong></td>
                  <td><strong>{totalBets}</strong></td>
                  <td><strong>{totalBets > 0 ? (totalValue / totalBets).toFixed(2) : '0.00'}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="svsmr-right">
            <div className="svsmr-lottery-box">
              <div className="svsmr-lottery-header">
                <div className="svsmr-lottery-title">{tirageName}</div>
                <div className="svsmr-lottery-actions">
                  <button className="svsmr-back-btn" onClick={onBack}>↩</button>
                  <button className="svsmr-menu-btn">☰<br/><span>Menu</span></button>
                </div>
              </div>
              <table className="svsmr-summary-table">
                <tbody>
                  {productSummary.map((row, i) => (
                    <tr key={i}>
                      <td className="svsmr-sum-label">{row.label}</td>
                      <td className="svsmr-sum-value">{row.value.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                  <tr className="svsmr-sum-total">
                    <td>Total:</td>
                    <td>{totalProduct.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="svsmr-bylottery-box">
              <div className="svsmr-bylottery-title">Summary by Lottery</div>
              <table className="svsmr-bylottery-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Lot</th>
                    <th>Description</th>
                    <th>Local</th>
                    <th>Online</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lotteryData.map((row, i) => (
                    <tr key={i}>
                      <td>🔒</td>
                      <td>{row.lot}</td>
                      <td>{row.desc}</td>
                      <td>{row.local.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td className="svsmr-online-val">{row.online.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td className="svsmr-online-val">{row.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                  <tr className="svsmr-bylottery-total">
                    <td></td>
                    <td colSpan="2"><strong>Total</strong></td>
                    <td>{totalLocal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className="svsmr-online-val">{totalLottery.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className="svsmr-online-val">{totalLottery.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </SupervisorLayout>
  );
};

export default SupervisorSalesMonitoringResults;
